import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../../services/api';
import { useToast } from "../common/ToastProvider";
interface HistoryVisitRow {
    visit_id: number;
    visit_date: string;
    visit_type: string;
    remarks?: string;
    complaint?: string;
    status: string;
    is_locked: boolean;
    name: string;
    patient_id: string;
}

interface VisitHistoryViewProps {
    onPatientSelect: (patient: { patient_id: string; name?: string; phone?: string }) => void;
    refreshKey?: number;
}

export default function VisitHistoryView({ onPatientSelect, refreshKey }: VisitHistoryViewProps) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [visits, setVisits] = useState<HistoryVisitRow[]>([]);
    const [error, setError] = useState('');

    const loadHistory = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await apiGet('/api/visits/history');
            if (Array.isArray(data)) {
                setVisits(data as HistoryVisitRow[]);
            } else {
                setVisits([]);
            }
        } catch (e: any) {
            showToast({ message: "Failed to load visit history.", type: "error" });
            setError('Failed to load visit history.');
            setVisits([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory, refreshKey]);

    // Group visits by date
    const grouped = visits.reduce((acc, visit) => {
        const date = visit.visit_date || 'Unknown Date';
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(visit);
        return acc;
    }, {} as Record<string, HistoryVisitRow[]>);

    return (
      <div className="ui-card rc-visit-history">
          <div className="rc-visit-history-header">
              <h3 className="rc-visit-history-title">All Visit History</h3>
              <p className="rc-visit-history-subtitle">Recent patient visits chronologically.</p>
          </div>
  
          <div className="rc-visit-history-body">
              {loading ? (
                  <div className="rc-visit-history-loading">Loading history...</div>
              ) : error ? (
                  <div className="rc-visit-history-error">{error}</div>
              ) : visits.length === 0 ? (
                  <div className="mod-empty-state rc-empty-tall">No visit history found.</div>
              ) : (
                  Object.entries(grouped).map(([dateStr, dateVisits]) => (
                      <div key={dateStr} className="rc-visit-group">
                          <div className="rc-visit-group-header">
                              <span className="rc-visit-group-icon">📅</span>
                              {new Date(dateStr).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          <div className="rc-visit-group-list">
                              {dateVisits.map((v) => (
                                  <div
                                      key={v.visit_id}
                                      className="rc-visit-item"
                                      onClick={() => {
                                          onPatientSelect({
                                              patient_id: v.patient_id,
                                              name: v.name,
                                              phone: v.patient_id
                                          });
                                      }}
                                  >
                                      <div>
                                          <div className="rc-visit-item-name">{v.name}</div>
                                          <div className="rc-visit-item-id">#{v.patient_id}</div>
                                      </div>
                                      <div className="rc-visit-item-meta">
                                          <span className={`rc-tag ${v.visit_type === 'New' ? "is-new" : "is-returning"}`}>
                                              {v.visit_type}
                                          </span>
                                          <div className="rc-visit-item-status">{v.status}</div>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      </div>
                  ))
              )}
          </div>
      </div>
    );
}
